import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { updatePersonalStat } from "../services/apiService";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PersonalStats = ({ data, onUpdate }) => {
  const [editableStats, setEditableStats] = useState({
    current: {},
    previous: {},
  });
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStat, setSelectedStat] = useState("");
  const [userToken, setUserToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const { user } = useAuth();

  // Default stats template for first-time users
  const defaultStatsTemplate = {
    height: 0,
    weight: 0,
    caloric_intake: 0,
    Neck: 0,
    Waist: 0,
    chest: 0,
    bicep_L: 0,
    bicep_R: 0,
    forearm_L: 0,
    forearm_R: 0,
    thigh_L: 0,
    thigh_R: 0,
    calf_L: 0,
    calf_R: 0,
  };

  useEffect(() => {
    if (user?.uid) setUserToken(user.uid);
  }, [user?.uid]);

  useEffect(() => {
    if (!data) {
      // No data at all - initialize with default template for first-time user
      setIsFirstTime(true);
      setEditableStats({
        current: { ...defaultStatsTemplate },
        previous: { ...defaultStatsTemplate },
      });
    } else if (data?.current && data?.previous) {
      // Check if user has existing data
      const hasCurrentData = Object.keys(data.current).length > 0;
      const hasPreviousData = Object.keys(data.previous).length > 0;

      if (!hasCurrentData && !hasPreviousData) {
        // First-time user - initialize with default template
        setIsFirstTime(true);
        setEditableStats({
          current: { ...defaultStatsTemplate },
          previous: { ...defaultStatsTemplate },
        });
      } else {
        setIsFirstTime(false);
        setEditableStats({
          current: { ...data.current },
          previous: { ...data.previous },
        });
      }
    } else if (data && (!data.current || !data.previous)) {
      // Partial data exists, fill missing parts
      setIsFirstTime(true);
      setEditableStats({
        current: data.current
          ? { ...data.current }
          : { ...defaultStatsTemplate },
        previous: data.previous
          ? { ...data.previous }
          : { ...defaultStatsTemplate },
      });
    }
  }, [data]);

  // Remove the duplicate useEffect for no data case

  const filterStats = (stats) => {
    const excludedKeys = ["id", "user_id", "updated_on"];
    return Object.keys(stats)
      .filter((key) => !excludedKeys.includes(key))
      .reduce((obj, key) => {
        obj[key] = stats[key];
        return obj;
      }, {});
  };

  const currentStats = filterStats(editableStats.current);
  const previousStats = filterStats(editableStats.previous);

  const labels = Array.from(
    new Set([...Object.keys(currentStats), ...Object.keys(previousStats)])
  );

  const personalStatsChartData = {
    labels: labels.map((label) =>
      label.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    ),
    datasets: [
      {
        label: "Current Stats",
        data: labels.map((label) => currentStats[label] || 0),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
      {
        label: "Previous Stats",
        data: labels.map((label) => previousStats[label] || 0),
        backgroundColor: "rgba(255, 99, 132, 0.6)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: isFirstTime
          ? "Personal Stats (Add Your First Entry)"
          : "Personal Stats Comparison",
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
        },
      },
    },
  };

  const handleInputChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    setEditableStats((prevState) => ({
      ...prevState,
      current: {
        ...prevState.current,
        [selectedStat]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!userToken) {
      toast.error("User Token is not defined!");
      return;
    }

    if (!selectedStat || editableStats.current[selectedStat] === undefined) {
      toast.error("Please select a stat and enter a value!");
      return;
    }

    const newValue = editableStats.current[selectedStat];

    if (newValue === null || newValue === undefined || newValue === "") {
      toast.error("Please enter a valid value!");
      return;
    }

    setLoading(true);

    try {
      // Send only the specific stat that was changed
      await updatePersonalStat(userToken, selectedStat, newValue);

      toast.success("Stat updated successfully!");
      setIsOpen(false);
      setSelectedStat("");
      setIsFirstTime(false);

      // Refresh the data to get updated values
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error updating stat:", error);
      toast.error("Failed to update stat. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleDropdown = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleStatSelect = (event) => {
    setSelectedStat(event.target.value);
  };

  return (
    <div className="personal-stats">
      <h2>Personal Statistics</h2>

      {isFirstTime && (
        <div className="first-time-notice">
          <p>
            <strong>Welcome!</strong> It looks like this is your first time
            adding personal stats. Please use the form below to add your
            measurements.
          </p>
        </div>
      )}

      <div className="chart-row">
        <div className="chart-container">
          <Bar data={personalStatsChartData} options={chartOptions} />
        </div>
      </div>

      <div className="stats-edit-container">
        <button className="toggle-btn" onClick={toggleDropdown}>
          {isOpen
            ? "Hide Edit Stats"
            : isFirstTime
            ? "Add Your Stats"
            : "Edit Stats"}
        </button>

        {isOpen && (
          <div className="stats-edit-form">
            <h3>{isFirstTime ? "Add Your First Stats" : "Edit Your Stats"}</h3>
            <label style={{ marginRight: "10px" }}>
              Select Stat to {isFirstTime ? "Add" : "Edit"}:{" "}
            </label>
            <select onChange={handleStatSelect} value={selectedStat}>
              <option value="">Select Stat</option>
              {Object.keys(defaultStatsTemplate).map((statKey) => (
                <option key={statKey} value={statKey}>
                  {statKey.replace(/_/g, " ").toUpperCase()}
                </option>
              ))}
            </select>

            {selectedStat && (
              <div className="input-group">
                <label>
                  {selectedStat.replace(/_/g, " ").toUpperCase()}{" "}
                  <span style={{ color: "#666" }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={editableStats.current[selectedStat] || ""}
                  onChange={handleInputChange}
                  placeholder="Enter value"
                />
                <small className="input-hint">
                  {selectedStat.includes("height") &&
                    "Enter height in feet (e.g., 5.8)"}
                  {selectedStat.includes("weight") && "Enter weight in kg"}
                  {selectedStat.includes("caloric") &&
                    "Enter daily caloric intake"}
                  {(selectedStat.includes("bicep") ||
                    selectedStat.includes("forearm") ||
                    selectedStat.includes("thigh") ||
                    selectedStat.includes("calf") ||
                    selectedStat.includes("Neck") ||
                    selectedStat.includes("Waist") ||
                    selectedStat.includes("chest")) &&
                    "Enter measurement in inches"}
                </small>

                {!isFirstTime && editableStats.current[selectedStat] && (
                  <small className="current-value-hint">
                    Current value: {editableStats.current[selectedStat]}
                    {editableStats.previous[selectedStat] &&
                      ` | Previous: ${editableStats.previous[selectedStat]}`}
                  </small>
                )}
              </div>
            )}

            <button
              className="submit-btn"
              onClick={handleSubmit}
              disabled={loading || !selectedStat}
            >
              {loading ? "Saving..." : isFirstTime ? "Add Stat" : "Update Stat"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalStats;
